import {List} from 'immutable';
import uniqueId from 'lodash/uniqueId';
import {Data, Int, RecordProps, Seconds} from '@store/@types/util';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {KeywordSearchResult, SpeakerNumber} from '@store/call-card/CallCard.types';
import {FilterWordDisplayWhen} from 'records/admin/topics/NewFilterWord';

export enum TopicType {
  TOPIC = 'TOPIC',
  KEYWORD = 'KEYWORD',
  TRANSCRIPT_WORD = 'TRANSCRIPT_WORD',
}

export enum SpeakerType {
  CALLER = 'CALLER',
  PROSPECT = 'PROSPECT',
  EITHER = 'EITHER',
}

export enum TopicFilter {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL',
  EITHER = 'EITHER',
}

export type TopicId = string;

interface TopicInterface {
  id: TopicId;
  name: string;
  color: string; // we make this (see the Source)
  occurrences: List<TopicOccurrence>;
  displayWhen: FilterWordDisplayWhen;
}

export class Topic extends Data<TopicInterface>({
  id: undefined,
  name: undefined,
  color: undefined,
  occurrences: List(),
  displayWhen: undefined,
}) {
  constructor(props: RecordProps<TopicInterface> = {}) {
    super({
      ...props,
      occurrences: List(props.occurrences?.map(o => new TopicOccurrence(o)) ?? []),
    });
  }
}

interface LegacyTopicInterface {
  id: TopicId;
  name: string;
  color: string; // we make this (see the Source)
  occurrences: List<LegacyTopicOccurrence>;
}

export class LegacyTopic extends Data<LegacyTopicInterface>({
  id: undefined,
  name: undefined,
  color: undefined,
  occurrences: List(),
}) {
  constructor(props: RecordProps<LegacyTopicInterface> = {}) {
    super({
      ...props,
      occurrences: List(props.occurrences?.map(o => new LegacyTopicOccurrence(o)) ?? []),
    });
  }
}

export class Keyword extends Data<TopicInterface>({
  id: undefined,
  name: undefined,
  color: undefined, // we make this (see the Source)
  occurrences: List(),
  displayWhen: undefined,
}) {
  constructor(props: RecordProps<TopicInterface> = {}) {
    super({
      ...props,
      occurrences: List(props.occurrences?.map(o => new TopicOccurrence(o)) ?? []),
    });
  }
}

type SearchId = TopicId | string;
interface SearchResultInterface {
  id: SearchId;
  name: string;
  word: string;
  topicId: TopicId;
  topicType: TopicType;
  speakerType: SpeakerType;
  occurrences: Int;
  color: string;
}

export class SearchResult extends Data<SearchResultInterface>({
  id: undefined,
  name: undefined,
  word: undefined,
  topicId: undefined,
  topicType: undefined,
  speakerType: undefined,
  occurrences: undefined,
  color: undefined,
}) {
  constructor(props: RecordProps<SearchResultInterface> = {}) {
    super({
      ...props,
      // not everything's a topic, but everything needs an id.
      id: props.topicId || uniqueId('transcript_search_'),
      name: props.word,
    });
  }
}

export type Matchable = Topic | KeywordSearchResult;

interface TopicOccurrenceInterface {
  topicId: TopicId;
  word: string;
  time: Seconds;
  speakerNumber: SpeakerNumber;
  color: string;
  type: TopicType;
}

// TopicOccurrence is actually used for all types (topic/keyword/freetext)
export class TopicOccurrence extends Data<TopicOccurrenceInterface>({
  topicId: undefined,
  word: undefined,
  time: undefined,
  speakerNumber: undefined,
  color: undefined,
  type: undefined,
}) {}

interface LegacyTopicOccurrenceInterface {
  topicId: TopicId;
  word: string;
  time: Seconds;
  speaker: SpeakerType;
  color: string;
  type: TopicType;
}

export class LegacyTopicOccurrence extends Data<LegacyTopicOccurrenceInterface>({
  topicId: undefined,
  word: undefined,
  time: undefined,
  speaker: undefined,
  color: undefined,
  type: undefined,
}) {}

interface TopicOccurrenceContextInterface {
  id: SearchId;
  word: string; // The word being matched
  words: string[]; // Chunk split into words
  color: string; // search color
  type: TopicType;
  speaker: SpeakerType;
  time: Seconds; // the time of the exact word
  startTime: Seconds; // the start time for the sentence (words)
  chunk: string; // raw sentence that was matched
  speakerNumber: SpeakerNumber;
}

// similar to above, this is actually used for topics/keywords/freetext
// this provides the "context" around the word
// use type and id to match
export class TopicOccurrenceContext extends Data<TopicOccurrenceContextInterface>({
  id: undefined,
  word: undefined,
  words: undefined,
  color: undefined,
  type: undefined,
  speaker: undefined,
  time: undefined,
  startTime: undefined,
  chunk: undefined,
  speakerNumber: undefined,
}) {}

export interface FetchTopicsResponse {
  callId: FileUploadId;
  topics: List<LegacyTopic>;
  extraTopics: List<TopicId>;
}

export interface FetchKeywordsResponse {
  callId: FileUploadId;
  keywords: List<Keyword>;
}

export interface SearchResponse {
  callId: FileUploadId;
  searchResults: List<SearchResult>;
}

export interface FreetextTranscriptSearchResponse {
  callId: FileUploadId;
  searchId: SearchId;
  searchResults: List<LegacyTopicOccurrence>;
  searchContext: List<TopicOccurrenceContext>;
}

export interface TopicTranscriptSearchResponse {
  callId: FileUploadId;
  topicId: TopicId;
  occurrences: List<TopicOccurrenceContext>;
}

export interface KeywordTranscriptSearchResponse {
  callId: FileUploadId;
  keywordId: TopicId;
  occurrences: List<TopicOccurrenceContext>;
}
